import { Roles } from '@omnea-digital/toolbox'
import { useQuery } from '@tanstack/react-query'
import { createContext, PropsWithChildren } from 'react'
import { AssessmentsHttpClient } from '@omnea-app/assessments/assessmentsHttpClient'
import { assessmentsQueryKey } from '@omnea-app/fe-assessments/useListAssessments'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { renewalKeys } from '@omnea-app/fe-renewals/query-key-factory'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { RenewalsHttpClient } from '@omnea-app/renewals/renewalsHttpClient'

interface StatsContextType {
  numRenewalsActionNeeded: number | null
  numAssessmentsActionNeeded: number | null
  loading: boolean
}

export const StatsContext = createContext<StatsContextType>({
  numRenewalsActionNeeded: null,
  numAssessmentsActionNeeded: null,
  loading: true,
})

const useRenewalStats = (userId: string, enabled: boolean) => {
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    enabled,
    queryKey: renewalKeys.stats(userId),
    queryFn: async () => {
      const authorization = await getToken()
      const renewalsApiClient = new RenewalsHttpClient({ authorization })

      const { data, error } = await renewalsApiClient.GET('/get-stats')

      if (error) throwIfApiError(error)

      return data.renewalStats
    },
  })
}

const useAssessmentStats = (userId: string, enabled: boolean) => {
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    enabled,
    queryKey: [assessmentsQueryKey, 'stats', userId],
    queryFn: async () => {
      const authorization = await getToken()
      const assessmentsApiClient = new AssessmentsHttpClient({ authorization })

      const { data, error } = await assessmentsApiClient.GET('/get-stats')

      if (error) throwIfApiError(error)

      return data.assessmentStats
    },
  })
}

export const StatsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuthenticatedUser()
  const { ENABLE_ASSESSMENTS } = useFeatureFlags()
  const hasRenewalsAccess = user.role === Roles.ADMIN || user.role === Roles.USER
  const isSupplier = user.role === Roles.SUPPLIER
  const assessmentsEnabled = !isSupplier && ENABLE_ASSESSMENTS

  const { data: renewalStats, isLoading: renewalStatsLoading } = useRenewalStats(user.id, hasRenewalsAccess)
  const { data: assessmentStats, isLoading: assessmentStatsLoading } = useAssessmentStats(user.id, assessmentsEnabled)

  const numRenewalsActionNeeded = renewalStats?.actionNeededCount ?? null
  const numAssessmentsActionNeeded = assessmentStats?.actionNeededCount ?? null
  const loading = renewalStatsLoading || assessmentStatsLoading

  return (
    <StatsContext.Provider
      value={{
        numRenewalsActionNeeded,
        numAssessmentsActionNeeded,
        loading,
      }}
    >
      {children}
    </StatsContext.Provider>
  )
}
